import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService, courseService } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authService.getMe();
      setUser(data.data.user);
      // Auto-sync courses when application is opened (user is authenticated)
      courseService.sync().catch(err => console.error('Course auto-sync on app load failed:', err));
    } catch {
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (credentials) => {
    const { data } = await authService.login(credentials);
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    setUser(data.data.user);
    courseService.sync().catch(err => console.error('Course auto-sync on login failed:', err));
    return data.data.user;
  };

  const googleLogin = async (credential) => {
    const { data } = await authService.googleLogin(credential);
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    setUser(data.data.user);
    courseService.sync().catch(() => {});
    return data.data.user;
  };

  const logout = async () => {
    try { await authService.logout(); } catch {}
    localStorage.clear();
    setUser(null);
  };

  const hasPermission = (module, action) => {
    if (!user?.role) return false;
    if (user.role.code === 'SUPER_ADMIN') return true;
    if (user.role.code === 'RETAIL_OU_LEAD' && module === 'leads') return true;
    const perm = user.role.permissions?.find(p => p.module === module);
    return perm?.actions?.[action] ?? false;
  };

  const hasRole = (...codes) => codes.includes(user?.role?.code);

  return (
    <AuthContext.Provider value={{ user, loading, login, googleLogin, logout, hasPermission, hasRole, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};
